import { useState, useCallback, useRef } from "react";

interface StagedExpressions {
  [key: string]: string;
}

interface UseV1ExpressionMigrationResult {
  stagedExpressions: StagedExpressions;
  originalPreviewExpressions: StagedExpressions;
  legacyWorkflowTypes: string[];
  isStaged: boolean;
  hasEdits: boolean;
  handleStageExpressions: (expressions: StagedExpressions) => void;
  handleRevertStaged: () => void;
  handleLegacyWorkflowsDetected: (types: string[]) => void;
  getStagedFormula: (workflowType: string) => string | undefined;
  getOriginalPreviewFormula: (workflowType: string) => string | undefined;
  shouldExpandWorkflow: (workflowType: string) => boolean;
  isWorkflowEdited: (workflowType: string) => boolean;
  updateStagedFormula: (workflowType: string, formula: string) => void;
  restoreOriginalPreview: (workflowType: string) => void;
  clearStagedFormula: (workflowType: string) => void;
}

/**
 * Hook to manage V1 expression migration state and logic.
 * Encapsulates all state needed for V1 migration - extract to remove feature later.
 */
export const useV1ExpressionMigration = (): UseV1ExpressionMigrationResult => {
  const [stagedExpressions, setStagedExpressions] = useState<StagedExpressions>(
    {}
  );
  const [originalPreviewExpressions, setOriginalPreviewExpressions] =
    useState<StagedExpressions>({});
  const [legacyWorkflowTypes, setLegacyWorkflowTypes] = useState<string[]>([]);
  const prevLegacyTypesRef = useRef<string[]>([]);

  const isStaged = Object.keys(stagedExpressions).length > 0;

  const hasEdits = Object.keys(stagedExpressions).some(
    (key) => stagedExpressions[key] !== originalPreviewExpressions[key]
  );

  const handleStageExpressions = useCallback(
    (expressions: StagedExpressions) => {
      setStagedExpressions(expressions);
      setOriginalPreviewExpressions(expressions);
    },
    []
  );

  const handleRevertStaged = useCallback(() => {
    setStagedExpressions({});
    setOriginalPreviewExpressions({});
  }, []);

  const handleLegacyWorkflowsDetected = useCallback((types: string[]) => {
    const prevTypes = prevLegacyTypesRef.current;
    // If the legacy workflow types changed, clear staged state
    // This handles the case where a workflow is saved and refetched
    const typesChanged =
      prevTypes.length !== types.length ||
      prevTypes.some((t) => !types.includes(t)) ||
      types.some((t) => !prevTypes.includes(t));

    if (typesChanged) {
      setStagedExpressions({});
      setOriginalPreviewExpressions({});
    }

    prevLegacyTypesRef.current = types;
    setLegacyWorkflowTypes(types);
  }, []);

  const getStagedFormula = useCallback(
    (workflowType: string): string | undefined => {
      if (!legacyWorkflowTypes.includes(workflowType)) {
        return undefined;
      }
      return stagedExpressions[workflowType];
    },
    [legacyWorkflowTypes, stagedExpressions]
  );

  const getOriginalPreviewFormula = useCallback(
    (workflowType: string): string | undefined => {
      return originalPreviewExpressions[workflowType];
    },
    [originalPreviewExpressions]
  );

  const shouldExpandWorkflow = useCallback(
    (workflowType: string): boolean => {
      return isStaged && legacyWorkflowTypes.includes(workflowType);
    },
    [isStaged, legacyWorkflowTypes]
  );

  const isWorkflowEdited = useCallback(
    (workflowType: string): boolean => {
      return (
        stagedExpressions[workflowType] !==
        originalPreviewExpressions[workflowType]
      );
    },
    [stagedExpressions, originalPreviewExpressions]
  );

  const updateStagedFormula = useCallback(
    (workflowType: string, formula: string) => {
      setStagedExpressions((prev) => ({
        ...prev,
        [workflowType]: formula,
      }));
    },
    []
  );

  const restoreOriginalPreview = useCallback(
    (workflowType: string) => {
      const original = originalPreviewExpressions[workflowType];
      if (original) {
        setStagedExpressions((prev) => ({
          ...prev,
          [workflowType]: original,
        }));
      }
    },
    [originalPreviewExpressions]
  );

  const clearStagedFormula = useCallback((workflowType: string) => {
    setStagedExpressions((prev) => {
      const { [workflowType]: _, ...rest } = prev;
      return rest;
    });
    setOriginalPreviewExpressions((prev) => {
      const { [workflowType]: _, ...rest } = prev;
      return rest;
    });
  }, []);

  return {
    stagedExpressions,
    originalPreviewExpressions,
    legacyWorkflowTypes,
    isStaged,
    hasEdits,
    handleStageExpressions,
    handleRevertStaged,
    handleLegacyWorkflowsDetected,
    getStagedFormula,
    getOriginalPreviewFormula,
    shouldExpandWorkflow,
    isWorkflowEdited,
    updateStagedFormula,
    restoreOriginalPreview,
    clearStagedFormula,
  };
};

/**
 * Get the workflow type from a workflow identifier
 * @param identifier - The workflow identifier (e.g., "applicant_workflow")
 * @returns The workflow type (applicant, occupant, guarantor, lead, or empty string)
 */
export const getWorkflowType = (identifier?: string): string => {
  const id = identifier?.toLowerCase() || "";
  if (id.includes("applicant")) return "applicant";
  if (id.includes("occupant")) return "occupant";
  if (id.includes("guarantor")) return "guarantor";
  if (id.includes("lead")) return "lead";
  return "";
};
